import { EXPECTATION_MAX_CHILDREN, EXPECTATION_MAX_MODULES } from './schemas/offer';

export const EXPECTATIONS_JSON_OPEN = '<EXPECTATIONS_JSON>';
export const EXPECTATIONS_JSON_CLOSE = '</EXPECTATIONS_JSON>';

export interface StreamingExpectationChild {
  title: string;
  description: string;
}

export interface StreamingExpectationParent {
  title: string;
  description: string;
  children: StreamingExpectationChild[];
}

function envelopeBody(rawText: string): string | null {
  const openIndex = rawText.indexOf(EXPECTATIONS_JSON_OPEN);
  if (openIndex === -1) return null;
  const from = openIndex + EXPECTATIONS_JSON_OPEN.length;
  const closeIndex = rawText.indexOf(EXPECTATIONS_JSON_CLOSE, from);
  return rawText.slice(from, closeIndex === -1 ? rawText.length : closeIndex);
}

function expectationsArrayStart(body: string): number {
  const key = body.indexOf('"expectations"');
  if (key === -1) return -1;
  const bracket = body.indexOf('[', key + '"expectations"'.length);
  return bracket === -1 ? -1 : bracket + 1;
}

function closedObjects(body: string, from: number, limit: number): string[] {
  const objects: string[] = [];
  let depth = 0;
  let start = -1;
  let inString = false;
  let escaped = false;
  for (let index = from; index < body.length; index += 1) {
    const char = body[index];
    if (inString) {
      if (escaped) escaped = false;
      else if (char === '\\') escaped = true;
      else if (char === '"') inString = false;
      continue;
    }
    if (char === '"') {
      inString = true;
      continue;
    }
    if (char === '{') {
      if (depth === 0) start = index;
      depth += 1;
      continue;
    }
    if (char === '}') {
      depth -= 1;
      if (depth < 0) break;
      if (depth === 0 && start !== -1) {
        objects.push(body.slice(start, index + 1));
        start = -1;
        if (objects.length >= limit) break;
      }
      continue;
    }
    if (char === ']' && depth === 0) break;
  }
  return objects;
}

function readText(source: Record<string, unknown>, key: string): string | null {
  const value = source[key];
  return typeof value === 'string' ? value : null;
}

function toChild(value: unknown): StreamingExpectationChild | null {
  if (typeof value !== 'object' || value === null) return null;
  const raw = value as Record<string, unknown>;
  const title = readText(raw, 'title');
  if (title === null || title.trim().length === 0) return null;
  return { title, description: readText(raw, 'description') ?? '' };
}

function toParent(value: unknown): StreamingExpectationParent | null {
  if (typeof value !== 'object' || value === null) return null;
  const raw = value as Record<string, unknown>;
  const title = readText(raw, 'title');
  if (title === null || title.trim().length === 0) return null;
  const rawChildren = Array.isArray(raw.children) ? raw.children : [];
  const children: StreamingExpectationChild[] = [];
  for (const candidate of rawChildren.slice(0, EXPECTATION_MAX_CHILDREN)) {
    const child = toChild(candidate);
    if (child) children.push(child);
  }
  return { title, description: readText(raw, 'description') ?? '', children };
}

export function parseStreamingExpectations(rawText: string): StreamingExpectationParent[] {
  const body = envelopeBody(rawText);
  if (body === null) return [];
  const start = expectationsArrayStart(body);
  if (start === -1) return [];
  const parents: StreamingExpectationParent[] = [];
  for (const chunk of closedObjects(body, start, EXPECTATION_MAX_MODULES)) {
    let parsed: unknown;
    try {
      parsed = JSON.parse(chunk);
    } catch {
      continue;
    }
    const parent = toParent(parsed);
    if (parent) parents.push(parent);
  }
  return parents;
}
